import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ImageIcon } from 'lucide-react';
import api from '@/lib/api';
import Footer from '@/components/Footer';
import { MainHeader } from '@/components/main/MainHeader';
import { FeatureCard, MainSellerStart } from '@/components/main/MainFeatures';
import { FEATURES, mediaUrl, useMainContent } from '@/components/main/mainContent';

// 이미지·영상 자리 · 영상 우선(음소거 자동재생) · 실패/미등록 시 이미지 · 둘 다 없으면 아이콘 자리
export const DetailMedia = ({ image, video, icon: Icon, testid, className = '' }) => {
  const [failed, setFailed] = useState(null);
  const v = mediaUrl(video);
  const img = mediaUrl(image);
  return (
    <div className={`relative w-full overflow-hidden rounded-[22px] bg-gradient-to-br from-[#EEF1FF] to-[#F3ECFF] ${className}`} data-testid={testid}>
      {v && failed !== v ? (
        <video key={v} src={v} poster={img || undefined} autoPlay muted loop playsInline onError={() => setFailed(v)}
          className="absolute inset-0 w-full h-full object-cover" />
      ) : img ? (
        <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#5B6BF5]/70">
          {Icon ? <Icon className="w-12 h-12 md:w-16 md:h-16" strokeWidth={1.5} /> : <ImageIcon className="w-12 h-12" />}
          <span className="text-[12px] text-[#8A90B8]">이미지 준비 중</span>
        </div>
      )}
    </div>
  );
};

const SubFeatures = ({ items, icon }) => {
  const [sel, setSel] = useState(0);
  const cur = items[sel] || items[0];
  if (!cur) return null;
  return (
    <section className="px-4 md:px-12 py-10 md:py-16 bg-[#F6F7FF]" data-testid="feature-sub-section">
      <div className="max-w-[1340px] mx-auto">
        <h2 className="text-[22px] md:text-[34px] font-black tracking-tight text-[#1B2140] mb-5 md:mb-8">세부 기능</h2>
        <div className="grid md:grid-cols-[280px_1fr] gap-4 md:gap-8">
          <div className="grid grid-cols-2 md:grid-cols-1 gap-2 content-start" role="tablist">
            {items.map((it, i) => (
              <button key={it.key} type="button" role="tab" aria-selected={i === sel} onClick={() => setSel(i)}
                data-testid={`feature-sub-tab-${it.key}`}
                className={`text-left rounded-xl px-3 md:px-4 py-3 text-[13px] md:text-[16px] font-bold break-keep border transition-colors ${i === sel ? 'bg-gradient-to-r from-[#1E5BFF] to-[#6D3DF5] text-white border-transparent' : 'bg-white text-[#1B2140] border-[#E4E7F6] hover:border-[#2F5BFF]'}`}>
                {it.name}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-[24px] border border-[#E4E7F6] p-3 md:p-6" data-testid="feature-sub-panel">
            <DetailMedia image={cur.image} video={cur.video} icon={icon} className="aspect-[16/9]" testid="feature-sub-media" />
            <h3 className="mt-4 md:mt-6 text-[19px] md:text-[26px] font-black text-[#1B2140]" data-testid="feature-sub-title">{cur.name}</h3>
            <p className="mt-2 text-[14px] md:text-[17px] text-[#5B6185] leading-relaxed whitespace-pre-line" data-testid="feature-sub-desc">{cur.desc}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const StartLink = () => (
  <Link to="/seller/apply" data-testid="feature-start-btn"
    className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-2xl bg-gradient-to-r from-[#19B6FF] via-[#3D6BFF] to-[#8B3DFF] text-white text-[17px] font-bold shadow-[0_8px_24px_rgba(61,107,255,0.35)] hover:brightness-110 transition-[filter]">
    판매자 시작하기 <ArrowRight className="w-5 h-5" />
  </Link>
);

export default function FeatureDetail({ user, slug, featureKey }) {
  const [page, setPage] = useState(null);
  const [error, setError] = useState(false);
  const mainContent = useMainContent();
  const feature = FEATURES.find((f) => f.key === featureKey);
  useEffect(() => {
    api.get(`/public/feature-pages/${slug}`).then((r) => setPage(r.data)).catch(() => setError(true));
  }, [slug]);
  const others = FEATURES.filter((f) => f.key !== featureKey);

  return (
    <div className="min-h-screen bg-white main-renewal-root" data-testid="feature-detail-page">
      <MainHeader user={user} />
      {error ? (
        <div className="px-6 py-24 text-center" data-testid="feature-detail-unavailable">
          <p className="text-[18px] font-bold text-[#1B2140]">준비 중인 페이지입니다.</p>
          <Link to="/" className="mt-4 inline-block text-[#2F5BFF] font-bold">메인으로 돌아가기</Link>
        </div>
      ) : !page ? <div className="h-[60vh]" /> : (
        <>
          <section className="bg-gradient-to-b from-[#F4F6FF] to-white px-4 md:px-12 pt-8 md:pt-14 pb-10 md:pb-16" data-testid="feature-hero">
            <div className="max-w-[1340px] mx-auto grid md:grid-cols-[1fr_1.4fr] gap-6 md:gap-12 items-center">
              <div className="main-fade-up">
                <p className="text-[#2F45E0] font-bold text-[14px] md:text-[17px]">LIVE 방송기능</p>
                <h1 className="mt-2 text-[32px] md:text-[48px] font-black tracking-tight text-[#1B2140]" data-testid="feature-title">{page.title}</h1>
                <p className="mt-3 text-[17px] md:text-[22px] font-bold text-[#3D4475] break-keep" data-testid="feature-tagline">{page.tagline}</p>
                <p className="mt-4 text-[14px] md:text-[16px] text-[#5B6185] leading-relaxed whitespace-pre-line" data-testid="feature-intro">{page.intro}</p>
                <div className="mt-6 md:mt-8"><StartLink /></div>
              </div>
              <DetailMedia image={page.hero_image} video={page.hero_video} icon={feature?.icon}
                className="aspect-[16/10] shadow-[0_20px_50px_rgba(40,50,140,0.15)]" testid="feature-hero-media" />
            </div>
          </section>
          <SubFeatures items={page.items} icon={feature?.icon} />
          {page.benefits?.length > 0 && (
            <section className="px-4 md:px-12 py-10 md:py-16" data-testid="feature-benefits">
              <div className="max-w-[1340px] mx-auto">
                <h2 className="text-[22px] md:text-[34px] font-black tracking-tight text-[#1B2140] mb-5 md:mb-8">주요 장점</h2>
                <ul className="grid md:grid-cols-3 gap-3 md:gap-5">
                  {page.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 rounded-2xl border border-[#E4E7F6] bg-white p-4 md:p-6 text-[14px] md:text-[17px] font-semibold text-[#1B2140] break-keep">
                      <Check className="shrink-0 w-5 h-5 text-[#2F5BFF] mt-0.5" />{b}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}
          <section className="px-4 md:px-12 py-10 md:py-16 bg-[#F6F7FF]" data-testid="feature-others">
            <div className="max-w-[1340px] mx-auto">
              <h2 className="text-[22px] md:text-[34px] font-black tracking-tight text-[#1B2140] mb-5 md:mb-8">다른 주요 기능</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-6">
                {others.map((f, i) => <FeatureCard key={f.key} f={f} index={i} image={mainContent?.card_images?.[f.key]} />)}
              </div>
            </div>
          </section>
          <MainSellerStart user={user} />
        </>
      )}
      <Footer user={user} />
    </div>
  );
}
